"use client";

import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  HStack,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  useToast,
  FormControl,
  FormLabel,
  Spinner,
  Center,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  SimpleGrid,
  TableContainer,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  List,
  ListItem,
  ListIcon,
  Progress,
  Tooltip,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  FiCheck,
  FiPackage,
  FiZap,
  FiShield,
  FiCalendar,
  FiAlertTriangle,
} from "react-icons/fi";
import Layout from "./Layout.tsx";
import { supabase } from "../supabase.ts";
import { useAppTheme } from "../ThemeContext.tsx";
import { useCreditsGate } from "../CreditsGateContext.tsx";

interface Subscription {
  id: number;
  user_id: string;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  stripe_price_id: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
}

interface CreditBalance {
  total_credits: number;
  used_credits: number;
  available_credits?: number;
  last_recharged_at: string;
}

interface Invoice {
  id: number;
  stripe_invoice_id: string;
  amount_due: number;
  amount_paid: number;
  status: string;
  hosted_invoice_url: string;
  invoice_pdf: string;
  receipt_url?: string;
  paid_at: string;
  created_at: string;
}

interface Transaction {
  id: number;
  amount: number;
  transaction_type: string;
  description: string;
  created_at: string;
}

function getMonthlySubscriptionUsdNumeric(): number {
  const raw = process.env.REACT_APP_STRIPE_MONTHLY_SUBSCRIPTION_PRICE_IN_USD;
  const n =
    raw != null && String(raw).trim() !== ""
      ? Number.parseFloat(String(raw))
      : NaN;
  return Number.isFinite(n) ? n : 500;
}

function parseEnvUsdPositive(key: string, fallback: number): number {
  const raw = process.env[key];
  const n =
    raw != null && String(raw).trim() !== ""
      ? Number.parseFloat(String(raw))
      : NaN;
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function formatUsdPrice(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatMonthlyUsd(): string {
  const v = getMonthlySubscriptionUsdNumeric();
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(v);
}

function getTransactionDescriptionLabel(description: string): string {
  const d = (description || "").toLowerCase();
  if (d.includes("voice") && d.includes("phone")) {
    return "Monthly connection fee - voice number";
  }
  return description || "—";
}

const Billing: React.FC = () => {
  const { resolvedTheme } = useAppTheme();
  const { refreshCredits } = useCreditsGate();
  const isDark = resolvedTheme === "dark";
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const pageBg = isDark ? "gray.900" : "gray.50";
  const cardBg = isDark ? "gray.800" : "white";
  const mutedColor = isDark ? "gray.400" : "gray.600";
  const headingColor = isDark ? "gray.100" : "gray.800";
  const tableHeadBg = isDark ? "gray.700" : "gray.50";
  const tableHoverBg = isDark ? "gray.700" : "gray.50";
  const borderMuted = isDark ? "gray.600" : "gray.200";

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [creditQuantity, setCreditQuantity] = useState<number>(100);
  const [checkingOut, setCheckingOut] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  const monthlySubscriptionUsd = getMonthlySubscriptionUsdNumeric();
  const perCreditUsd = parseEnvUsdPositive(
    "REACT_APP_PER_CREDIT_PRICE_IN_USD",
    1,
  );
  const phoneMonthlyUsd = parseEnvUsdPositive(
    "REACT_APP_PER_PHONE_NUMBER_MONTHLY_PRICE_IN_USD",
    7,
  );
  const callMinuteUsd = parseEnvUsdPositive(
    "REACT_APP_PER_PHONE_CALL_MINUTE_PRICE_IN_USD",
    0.25,
  );
  const monthlyPriceLabel = formatMonthlyUsd();
  const subscriptionCreditsPerMonth = Math.max(
    0,
    Math.floor(monthlySubscriptionUsd / perCreditUsd),
  );
  const monthlyPhoneCreditsLabel =
    Math.round((phoneMonthlyUsd / perCreditUsd) * 100) / 100;

  const calculateCost = (quantity: number) =>
    (quantity * perCreditUsd).toFixed(2);
  const perCreditPriceLabel = formatUsdPrice(perCreditUsd);
  const callMinutePriceLabel = formatUsdPrice(callMinuteUsd);
  const phoneMonthlyPriceLabel = formatUsdPrice(phoneMonthlyUsd);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (cancelled) return;
      setUserId(user?.id ?? null);
      if (!user) {
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!userId) return;
    const success = searchParams.get("success");
    const sessionId = searchParams.get("session_id");
    if (success !== "true" || !sessionId) return;

    fetch("/api/billing/confirm-checkout-session", {
      method: "POST",
      headers: {
        "user-id": userId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.creditsAdded > 0) {
          toast({
            title: "Credits added",
            description: `${data.creditsAdded} credits have been added to your account.`,
            status: "success",
            duration: 4000,
          });
        }
        setSearchParams({});
        navigate("/billing", { replace: true });
      })
      .catch(() => {})
      .finally(() => fetchBillingData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, userId]);

  useEffect(() => {
    if (userId) fetchBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchBillingData = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const headers: Record<string, string> = { "user-id": userId };

      try {
        await fetch("/api/billing/sync-subscription", {
          method: "POST",
          headers,
        });
      } catch {
        /* subscription may not exist yet */
      }

      const subResponse = await fetch("/api/billing/subscription", { headers });
      const subData = await subResponse.json();
      setSubscription(subData.subscription);
      setCredits(subData.credits);

      const invoicesResponse = await fetch("/api/billing/invoices", {
        headers,
      });
      const invoicesData = await invoicesResponse.json();
      setInvoices(invoicesData.invoices || []);

      const transactionsResponse = await fetch(
        "/api/billing/transactions?limit=20",
        { headers },
      );
      const transactionsData = await transactionsResponse.json();
      setTransactions(transactionsData.transactions || []);
    } catch (error) {
      console.error("Error fetching billing data:", error);
      toast({
        title: "Error",
        description: "Failed to load billing data",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
      void refreshCredits();
    }
  };

  const handleSubscriptionCheckout = async () => {
    if (!userId) return;
    try {
      setCheckingOut(true);
      const response = await fetch(
        "/api/billing/create-subscription-checkout",
        {
          method: "POST",
          headers: {
            "user-id": userId,
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else if (data.error) {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast({
        title: "Error",
        description: "Failed to start checkout",
        status: "error",
        duration: 3000,
      });
    } finally {
      setCheckingOut(false);
    }
  };

  const hasActiveSubscription =
    Boolean(subscription?.stripe_subscription_id) &&
    subscription?.status === "active";

  const handleCreditsCheckout = async () => {
    if (!userId) return;

    if (!hasActiveSubscription) {
      toast({
        title: "Subscription required",
        description:
          "You need an active monthly subscription before purchasing additional credits.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (creditQuantity < 100) {
      toast({
        title: "Invalid Quantity",
        description: "Minimum 100 credits required",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    try {
      setCheckingOut(true);
      const response = await fetch("/api/billing/create-credits-checkout", {
        method: "POST",
        headers: {
          "user-id": userId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ quantity: creditQuantity }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else if (data.error) {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to start checkout",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCheckingOut(false);
    }
  };

  const handleReactivate = async () => {
    if (!userId) return;

    try {
      setReactivateLoading(true);

      const response = await fetch(
        "/api/billing/create-reactivation-checkout",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "user-id": userId,
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.url) {
        throw new Error(data.error || "Failed to create reactivation checkout");
      }

      window.location.href = data.url;
    } catch (error) {
      console.error("Error reactivating subscription:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to start reactivation checkout",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setReactivateLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!userId) return;
    if (
      !window.confirm(
        "Are you sure you want to cancel your subscription? Your subscription will remain active until the end of the current billing period.",
      )
    ) {
      return;
    }
    try {
      setCancelLoading(true);
      const response = await fetch("/api/billing/cancel-subscription", {
        method: "POST",
        headers: {
          "user-id": userId,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        toast({
          title: "Subscription Cancelled",
          description:
            "Your subscription will cancel at the end of the billing period",
          status: "success",
          duration: 5000,
        });
        fetchBillingData();
      }
    } catch (error) {
      console.error("Error canceling subscription:", error);
      toast({
        title: "Error",
        description: "Failed to cancel subscription",
        status: "error",
        duration: 3000,
      });
    } finally {
      setCancelLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "paid":
        return "green";
      case "past_due":
      case "unpaid":
        return "red";
      case "canceled":
        return "orange";
      case "trialing":
        return "blue";
      default:
        return "gray";
    }
  };

  const availableCredits = credits
    ? credits.total_credits - credits.used_credits
    : 0;
  const creditUsagePercentage =
    credits && credits.total_credits > 0
      ? (credits.used_credits / credits.total_credits) * 100
      : 0;

  if (!userId && !loading) {
    return (
      <Layout>
        <Center minH="calc(100vh - 60px)" bg={pageBg} px={4}>
          <Text color={mutedColor}>Please sign in to view billing.</Text>
        </Center>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <Center minH="100vh" bg={pageBg}>
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.400" />
            <Text color={mutedColor}>Loading...</Text>
          </VStack>
        </Center>
      </Layout>
    );
  }

  const statusColor = getStatusColor(
    hasActiveSubscription ? "active" : "inactive",
  );

  return (
    <Layout>
      <Box minH="100vh" bg={pageBg}>
        <Container maxW="7xl" py={8}>
          <VStack spacing={6} align="stretch">
            <Box>
              <Heading size="xl" color={headingColor}>
                Billing & Subscription
              </Heading>
              <Text mt={2} color={mutedColor} fontSize="sm">
                Manage your plan, credits, and billing history in one place.
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
              <Card
                bg={cardBg}
                borderWidth="1px"
                borderColor={borderMuted}
                transition="all 0.3s"
                _hover={{ transform: "translateY(-4px)", shadow: "lg" }}
              >
                <CardBody>
                  <Stat>
                    <HStack spacing={3} align="center">
                      <Box
                        p={2}
                        borderRadius="full"
                        bg={isDark ? "blue.900" : "blue.50"}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Icon as={FiPackage} color="blue.400" boxSize={5} />
                      </Box>
                      <StatLabel color={mutedColor}>Current Plan</StatLabel>
                    </HStack>
                    <StatNumber fontSize="2xl" mt={3} color={headingColor}>
                      {hasActiveSubscription ? "Monthly" : "Pay-as-you-go"}
                    </StatNumber>
                    <StatHelpText color={mutedColor}>
                      {hasActiveSubscription
                        ? `Includes ${subscriptionCreditsPerMonth.toLocaleString()} credits each month`
                        : "No recurring subscription"}
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>

              <Card
                bg={cardBg}
                borderWidth="1px"
                borderColor={borderMuted}
                transition="all 0.3s"
                _hover={{ transform: "translateY(-4px)", shadow: "lg" }}
              >
                <CardBody>
                  <Stat>
                    <HStack spacing={3} align="center">
                      <Box
                        p={2}
                        borderRadius="full"
                        bg={isDark ? "green.900" : "green.50"}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Icon as={FiZap} color="green.400" boxSize={5} />
                      </Box>
                      <StatLabel color={mutedColor}>
                        Credits Remaining
                      </StatLabel>
                    </HStack>
                    <StatNumber
                      fontSize="2xl"
                      mt={3}
                      color={availableCredits < 100 ? "red.400" : "green.400"}
                    >
                      {availableCredits.toFixed(1).toLocaleString()}
                    </StatNumber>
                    <StatHelpText color={mutedColor}>
                      {credits
                        ? `${credits.used_credits.toLocaleString()} of ${credits.total_credits.toLocaleString()} used`
                        : "No credits yet"}
                    </StatHelpText>
                    {credits && credits.total_credits > 0 && (
                      <Progress
                        mt={3}
                        size="sm"
                        borderRadius="full"
                        value={creditUsagePercentage}
                        colorScheme={availableCredits < 100 ? "red" : "green"}
                        bg={isDark ? "gray.700" : "gray.100"}
                      />
                    )}
                  </Stat>
                </CardBody>
              </Card>

              <Card
                bg={cardBg}
                borderWidth="1px"
                borderColor={borderMuted}
                transition="all 0.3s"
                _hover={{ transform: "translateY(-4px)", shadow: "lg" }}
              >
                <CardBody>
                  <Stat>
                    <HStack spacing={3} align="center">
                      <Box
                        p={2}
                        borderRadius="full"
                        bg={isDark ? `${statusColor}.900` : `${statusColor}.50`}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Icon
                          as={FiShield}
                          color={`${statusColor}.400`}
                          boxSize={5}
                        />
                      </Box>
                      <StatLabel color={mutedColor}>
                        Subscription Status
                      </StatLabel>
                    </HStack>
                    <StatNumber fontSize="2xl" mt={3}>
                      <Badge
                        px={3}
                        py={1}
                        colorScheme={statusColor}
                        fontSize="md"
                        textTransform="capitalize"
                      >
                        {hasActiveSubscription ? "ACTIVE" : "INACTIVE"}
                      </Badge>
                    </StatNumber>
                    <StatHelpText color={mutedColor}>
                      {subscription?.cancel_at_period_end
                        ? "Will cancel at period end"
                        : hasActiveSubscription
                          ? "Billing is up to date"
                          : "Subscribe to unlock monthly credits"}
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>

              <Card
                bg={cardBg}
                borderWidth="1px"
                borderColor={borderMuted}
                transition="all 0.3s"
                _hover={{ transform: "translateY(-4px)", shadow: "lg" }}
              >
                <CardBody>
                  <Stat>
                    <HStack spacing={3} align="center">
                      <Box
                        p={2}
                        borderRadius="full"
                        bg={isDark ? "purple.900" : "purple.50"}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Icon as={FiCalendar} color="purple.400" boxSize={5} />
                      </Box>
                      <StatLabel color={mutedColor}>
                        {subscription?.cancel_at_period_end
                          ? "Ends On"
                          : "Renews On"}
                      </StatLabel>
                    </HStack>
                    <StatNumber fontSize="xl" mt={3} color={headingColor}>
                      {subscription?.current_period_end
                        ? new Date(
                            subscription.current_period_end,
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "N/A"}
                    </StatNumber>
                    <StatHelpText color={mutedColor}>
                      {subscription?.cancel_at_period_end
                        ? "Subscription end date"
                        : "Next billing date"}
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </SimpleGrid>

            {hasActiveSubscription && subscription?.cancel_at_period_end && (
              <Alert
                status="info"
                borderRadius="md"
                variant="left-accent"
                bg={isDark ? "gray.800" : undefined}
              >
                <AlertIcon />
                <HStack
                  justify="space-between"
                  align="flex-start"
                  width="full"
                  flexWrap="wrap"
                  gap={3}
                >
                  <Box flex="1">
                    <AlertTitle>Subscription Ending</AlertTitle>
                    <AlertDescription color={mutedColor}>
                      Your subscription will end on{" "}
                      {subscription?.current_period_end
                        ? new Date(
                            subscription.current_period_end,
                          ).toLocaleDateString()
                        : "the end of your billing period"}
                      . Reactivate to continue your plan.
                    </AlertDescription>
                  </Box>
                  <Button
                    colorScheme="blue"
                    size="sm"
                    onClick={handleReactivate}
                    isLoading={reactivateLoading}
                  >
                    Reactivate Subscription
                  </Button>
                </HStack>
              </Alert>
            )}

            {credits && availableCredits < 100 && (
              <Alert
                status="warning"
                borderRadius="md"
                variant="left-accent"
                bg={isDark ? "gray.800" : undefined}
              >
                <AlertIcon as={FiAlertTriangle} />
                <Box>
                  <AlertTitle>Low Credit Balance!</AlertTitle>
                  <AlertDescription color={mutedColor}>
                    You have only{" "}
                    <Text as="span" fontWeight="bold">
                      {availableCredits}
                    </Text>{" "}
                    credits remaining.{" "}
                    {hasActiveSubscription
                      ? "Purchase additional credits below or reduce usage to avoid service interruption."
                      : "Subscribe to a monthly plan below to add credits and avoid service interruption."}
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            {!hasActiveSubscription ? (
              <Card
                maxW="lg"
                borderWidth={2}
                borderColor="blue.400"
                bg={cardBg}
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-4px)",
                  shadow: "xl",
                  borderColor: "blue.300",
                }}
              >
                <CardHeader bg={isDark ? "blue.900" : "blue.50"}>
                  <VStack align="start" spacing={2}>
                    <HStack justify="space-between" width="full">
                      <Heading
                        size="md"
                        color={isDark ? "blue.100" : "blue.800"}
                      >
                        Monthly Subscription
                      </Heading>
                      <HStack>
                        <Text
                          fontSize="2xl"
                          fontWeight="bold"
                          color={isDark ? "blue.100" : "blue.800"}
                        >
                          {monthlyPriceLabel}
                        </Text>
                        <Text fontSize="lg" color={mutedColor}>
                          /month
                        </Text>
                      </HStack>
                    </HStack>
                  </VStack>
                </CardHeader>
                <CardBody>
                  <List spacing={3} mb={6} color={headingColor}>
                    <ListItem>
                      <ListIcon as={FiCheck} color="green.400" />
                      Credits refresh monthly
                    </ListItem>
                    <ListItem>
                      <ListIcon as={FiCheck} color="green.400" />
                      Purchase additional credits with an active subscription
                    </ListItem>
                    <ListItem>
                      <ListIcon as={FiCheck} color="green.400" />
                      Cancel anytime
                    </ListItem>
                  </List>
                  <Button
                    colorScheme="blue"
                    size="lg"
                    width="full"
                    onClick={handleSubscriptionCheckout}
                    isLoading={checkingOut}
                    loadingText="Processing..."
                  >
                    Subscribe Now
                  </Button>
                </CardBody>
              </Card>
            ) : (
              <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
                <Card bg={cardBg} borderWidth="1px" borderColor={borderMuted}>
                  <CardHeader>
                    <Heading size="md" color={headingColor}>
                      Subscription Management
                    </Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack align="stretch" spacing={4}>
                      <Box>
                        <Text
                          fontWeight="bold"
                          fontSize="lg"
                          mb={1}
                          color={headingColor}
                        >
                          Monthly Subscription
                        </Text>
                        <Text
                          fontSize="2xl"
                          fontWeight="bold"
                          color="blue.300"
                          mb={2}
                        >
                          {monthlyPriceLabel}
                          <Text
                            as="span"
                            fontSize="md"
                            color={mutedColor}
                            fontWeight="normal"
                          >
                            /month
                          </Text>
                        </Text>
                        <Text fontSize="sm" color={mutedColor}>
                          Includes{" "}
                          {subscriptionCreditsPerMonth.toLocaleString()} credits
                          per month.
                        </Text>
                      </Box>

                      {subscription?.cancel_at_period_end ? (
                        <Alert
                          status="warning"
                          borderRadius="md"
                          variant="left-accent"
                          bg={isDark ? "gray.800" : undefined}
                        >
                          <AlertIcon />
                          <HStack
                            justify="space-between"
                            align="flex-start"
                            width="full"
                            flexWrap="wrap"
                            gap={3}
                          >
                            <Box flex="1">
                              <Text fontWeight="bold">Subscription Ending</Text>
                              <Text fontSize="sm" color={mutedColor}>
                                Your subscription will end on{" "}
                                {subscription?.current_period_end
                                  ? new Date(
                                      subscription.current_period_end,
                                    ).toLocaleDateString()
                                  : ""}
                                .
                              </Text>
                            </Box>
                            <Button
                              colorScheme="blue"
                              size="sm"
                              onClick={handleReactivate}
                              isLoading={reactivateLoading}
                            >
                              Reactivate Subscription
                            </Button>
                          </HStack>
                        </Alert>
                      ) : (
                        <>
                          <Divider borderColor={borderMuted} />
                          <Button
                            colorScheme="red"
                            variant="outline"
                            onClick={handleCancelSubscription}
                            isLoading={cancelLoading}
                            loadingText="Cancelling..."
                            width="full"
                          >
                            Cancel Subscription
                          </Button>
                          <Text fontSize="xs" color={mutedColor}>
                            Your subscription will remain active until the end
                            of the current billing period.
                          </Text>
                        </>
                      )}
                    </VStack>
                  </CardBody>
                </Card>

                <Card bg={cardBg} borderWidth="1px" borderColor={borderMuted}>
                  <CardHeader>
                    <Heading size="md" color={headingColor}>
                      Buy Additional Credits
                    </Heading>
                    <Text fontSize="sm" color={mutedColor} mt={2}>
                      Need more credits this month? Purchase additional credits
                      anytime.
                    </Text>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel fontSize="sm" color={mutedColor}>
                          Number of Credits
                        </FormLabel>
                        <NumberInput
                          min={100}
                          value={creditQuantity}
                          onChange={(valueString) =>
                            setCreditQuantity(
                              Number.parseInt(valueString, 10) || 100,
                            )
                          }
                          isDisabled={checkingOut}
                        >
                          <NumberInputField
                            bg={isDark ? "gray.700" : "white"}
                          />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                      <Text fontSize="sm" color={mutedColor}>
                        {perCreditPriceLabel} per credit • Minimum 100 credits •
                        No expiration
                      </Text>
                      <Button
                        colorScheme="green"
                        size="lg"
                        onClick={handleCreditsCheckout}
                        isDisabled={creditQuantity < 100 || checkingOut}
                        isLoading={checkingOut}
                        loadingText="Processing..."
                      >
                        Buy for ${calculateCost(creditQuantity)}
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              </Grid>
            )}

            <Divider borderColor={borderMuted} />

            <Box>
              <Heading size="md" mb={4} color={headingColor}>
                Pricing Information
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <Box
                  p={6}
                  bg={isDark ? "blue.900" : "blue.50"}
                  borderRadius="lg"
                  borderWidth={1}
                  borderColor={isDark ? "blue.700" : "blue.100"}
                >
                  <Text
                    fontWeight="bold"
                    color={isDark ? "blue.100" : "blue.800"}
                    mb={2}
                  >
                    Voice Minutes
                  </Text>
                  <Text
                    fontSize="3xl"
                    fontWeight="bold"
                    color="blue.400"
                    mb={1}
                  >
                    {callMinutePriceLabel}
                  </Text>
                  <Text fontSize="sm" color={isDark ? "blue.200" : "blue.700"}>
                    Per voice call minute
                  </Text>
                </Box>

                <Box
                  p={6}
                  bg={isDark ? "green.900" : "green.50"}
                  borderRadius="lg"
                  borderWidth={1}
                  borderColor={isDark ? "green.700" : "green.100"}
                >
                  <Text
                    fontWeight="bold"
                    color={isDark ? "green.100" : "green.800"}
                    mb={2}
                  >
                    Additional Credits
                  </Text>
                  <Text
                    fontSize="3xl"
                    fontWeight="bold"
                    color="green.400"
                    mb={1}
                  >
                    {perCreditPriceLabel}
                  </Text>
                  <Text
                    fontSize="sm"
                    color={isDark ? "green.200" : "green.700"}
                  >
                    Per credit, minimum 100 • Requires active subscription
                  </Text>
                </Box>

                <Tooltip
                  label="Each voice phone number. Billed on the 1st; prorated when added mid-month."
                  hasArrow
                  placement="top"
                  openDelay={300}
                  maxW="320px"
                >
                  <Box
                    p={6}
                    bg={isDark ? "orange.900" : "orange.50"}
                    borderRadius="lg"
                    borderWidth={1}
                    borderColor={isDark ? "orange.700" : "orange.100"}
                    cursor="pointer"
                  >
                    <Text
                      fontWeight="bold"
                      color={isDark ? "orange.100" : "orange.800"}
                      mb={2}
                    >
                      Phone numbers
                    </Text>
                    <Text
                      fontSize="3xl"
                      fontWeight="bold"
                      color="orange.400"
                      mb={1}
                    >
                      {phoneMonthlyPriceLabel}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={isDark ? "orange.200" : "orange.700"}
                    >
                      Per month, per active phone number (
                      {monthlyPhoneCreditsLabel.toLocaleString()} credits)
                    </Text>
                  </Box>
                </Tooltip>
              </SimpleGrid>
            </Box>

            <Box>
              <Heading size="md" mb={4} color={headingColor}>
                Recent Credit Transactions
              </Heading>
              <Card bg={cardBg} borderWidth="1px" borderColor={borderMuted}>
                <CardBody p={0}>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead bg={tableHeadBg}>
                        <Tr>
                          <Th color={headingColor}>Date</Th>
                          <Th color={headingColor}>Type</Th>
                          <Th color={headingColor}>Description</Th>
                          <Th isNumeric color={headingColor}>
                            Credits
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {transactions.length === 0 ? (
                          <Tr>
                            <Td
                              colSpan={4}
                              textAlign="center"
                              color={mutedColor}
                              py={8}
                            >
                              No transactions yet
                            </Td>
                          </Tr>
                        ) : (
                          transactions.slice(0, 10).map((transaction) => {
                            const isProratedUsage =
                              transaction.transaction_type === "usage" &&
                              transaction.amount < 0 &&
                              Math.abs(transaction.amount) <
                                monthlyPhoneCreditsLabel;
                            return (
                              <Tr
                                key={transaction.id}
                                _hover={{ bg: tableHoverBg }}
                              >
                                <Td color={headingColor}>
                                  {new Date(
                                    transaction.created_at,
                                  ).toLocaleDateString()}
                                </Td>
                                <Td>
                                  <Badge
                                    colorScheme={
                                      transaction.amount > 0
                                        ? "green"
                                        : "orange"
                                    }
                                  >
                                    {transaction.transaction_type}
                                  </Badge>
                                </Td>
                                <Td color={mutedColor}>
                                  {getTransactionDescriptionLabel(
                                    transaction.description,
                                  )}
                                </Td>
                                <Td
                                  isNumeric
                                  fontWeight="bold"
                                  color={
                                    transaction.amount > 0
                                      ? "green.400"
                                      : "orange.400"
                                  }
                                >
                                  {isProratedUsage ? (
                                    <Tooltip
                                      label={`Prorated because this phone number was added mid-month. Full monthly fee (${monthlyPhoneCreditsLabel.toLocaleString()} credits) is charged on the 1st for all active phone numbers.`}
                                      hasArrow
                                      placement="top"
                                      openDelay={300}
                                      maxW="280px"
                                    >
                                      <Box
                                        as="span"
                                        cursor="help"
                                        borderBottomWidth="1px"
                                        borderBottomStyle="dotted"
                                      >
                                        {transaction.amount > 0 ? "+" : ""}
                                        {transaction.amount}
                                      </Box>
                                    </Tooltip>
                                  ) : (
                                    <>
                                      {transaction.amount > 0 ? "+" : ""}
                                      {transaction.amount}
                                    </>
                                  )}
                                </Td>
                              </Tr>
                            );
                          })
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </Box>

            {invoices.length > 0 && (
              <Box>
                <Heading size="md" mb={4} color={headingColor}>
                  Billing History
                </Heading>
                <Card bg={cardBg} borderWidth="1px" borderColor={borderMuted}>
                  <CardBody p={0}>
                    <TableContainer>
                      <Table variant="simple" size="sm">
                        <Thead bg={tableHeadBg}>
                          <Tr>
                            <Th color={headingColor}>Date</Th>
                            <Th isNumeric color={headingColor}>
                              Amount
                            </Th>
                            <Th color={headingColor}>Status</Th>
                            <Th color={headingColor}>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {invoices.map((invoice) => (
                            <Tr key={invoice.id} _hover={{ bg: tableHoverBg }}>
                              <Td color={headingColor}>
                                {new Date(
                                  invoice.created_at,
                                ).toLocaleDateString()}
                              </Td>
                              <Td isNumeric fontWeight="bold" color="green.400">
                                ${invoice.amount_paid.toFixed(2)}
                              </Td>
                              <Td>
                                <Badge
                                  textTransform="capitalize"
                                  colorScheme={getStatusColor(invoice.status)}
                                >
                                  {invoice.status}
                                </Badge>
                              </Td>
                              <Td>
                                {invoice.hosted_invoice_url && (
                                  <Button
                                    as="a"
                                    href={invoice.hosted_invoice_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    leftIcon={<ExternalLinkIcon />}
                                    size="sm"
                                    variant="outline"
                                    colorScheme="blue"
                                  >
                                    View Invoice
                                  </Button>
                                )}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </CardBody>
                </Card>
              </Box>
            )}
          </VStack>
        </Container>
      </Box>
    </Layout>
  );
};

export default Billing;
